import { Link, Outlet, useLocation } from 'react-router-dom';
import { PowerOff } from 'lucide-react';
import { canAnywhere, type ModuleKey } from '@hrms/shared';
import { Button, EmptyState } from '@hrms/ui';
import { MODULE_LABELS } from '../lib/moduleLabels.js';
import { useAuthenticatedSession } from '../session/SessionProvider.js';
import { switchedOffModuleFor } from './moduleRoutes.js';

/**
 * What a switched-off module looks like to someone who followed a link to it: a plain explanation,
 * and a way forward for whoever can turn it back on. Replaces a page that would load and then fail on
 * every request.
 */
export function ModuleOffPage({ module, canSwitchOn }: { module: ModuleKey; canSwitchOn: boolean }) {
  const label = MODULE_LABELS[module];
  return (
    <EmptyState
      icon={<PowerOff className="size-8" aria-hidden="true" />}
      title={`${label} is not turned on for your company`}
      description={
        canSwitchOn
          ? 'An administrator turned this off, or it is not in your plan. You can turn it back on from Settings > Modules.'
          : 'It is not available to your company right now. Ask your administrator if you need it.'
      }
      action={
        canSwitchOn ? (
          <Link to="/admin/settings/modules">
            <Button variant="primary">Open Modules settings</Button>
          </Link>
        ) : (
          <Link to="/">
            <Button variant="secondary">Back to Home</Button>
          </Link>
        )
      }
    />
  );
}

/**
 * The app layout's `<Outlet />`, with the module check in front: a page that belongs to a module the
 * company has switched off shows `ModuleOffPage` instead. The module switch is only half of "may I see
 * this"; the person's own permissions still apply on top, in the menu and on every page behind it.
 */
export function RequireModuleOutlet() {
  const { pathname } = useLocation();
  const { actor, me } = useAuthenticatedSession();
  const off = switchedOffModuleFor(pathname, me.enabledModules);
  if (off) return <ModuleOffPage module={off} canSwitchOn={canAnywhere(actor, 'org.billing:update')} />;
  return <Outlet />;
}
